import { ChevronLeft, ChevronRight } from 'lucide-react';
import {
    DayFlag,
    DayPicker,
    SelectionState,
    UI,
    type DayPickerProps,
} from 'react-day-picker';

import { buttonVariants } from './Button';
import { cn } from '../../utils';

export type CalendarProps = DayPickerProps;

function Calendar({
    className,
    classNames,
    showOutsideDays = true,
    ...props
}: CalendarProps) {
    return (
        <DayPicker
            showOutsideDays={showOutsideDays}
            className={cn('p-3', className)}
            classNames={{
                [UI.Root]: cn('w-fit', classNames?.[UI.Root]),
                [UI.Months]: cn(
                    'relative flex flex-col gap-4 sm:flex-row',
                    classNames?.[UI.Months],
                ),
                [UI.Month]: cn('space-y-4', classNames?.[UI.Month]),
                [UI.MonthCaption]: cn(
                    'flex h-8 items-center justify-center',
                    classNames?.[UI.MonthCaption],
                ),
                [UI.CaptionLabel]: cn(
                    'text-sm font-semibold',
                    classNames?.[UI.CaptionLabel],
                ),
                [UI.Nav]: cn(
                    'absolute inset-x-0 top-0 flex items-center justify-between',
                    classNames?.[UI.Nav],
                ),
                [UI.PreviousMonthButton]: cn(
                    buttonVariants({ variant: 'ghost', size: 'icon' }),
                    'h-8 w-8 bg-transparent p-0 opacity-60 hover:opacity-100',
                    classNames?.[UI.PreviousMonthButton],
                ),
                [UI.NextMonthButton]: cn(
                    buttonVariants({ variant: 'ghost', size: 'icon' }),
                    'h-8 w-8 bg-transparent p-0 opacity-60 hover:opacity-100',
                    classNames?.[UI.NextMonthButton],
                ),
                [UI.MonthGrid]: cn(
                    'w-full border-collapse',
                    classNames?.[UI.MonthGrid],
                ),
                [UI.Weekdays]: cn('flex', classNames?.[UI.Weekdays]),
                [UI.Weekday]: cn(
                    'w-9 rounded-md text-center text-[0.8rem] font-normal text-muted-foreground',
                    classNames?.[UI.Weekday],
                ),
                [UI.Week]: cn('mt-2 flex w-full', classNames?.[UI.Week]),
                [UI.Day]: cn(
                    'relative h-9 w-9 p-0 text-center text-sm focus-within:relative focus-within:z-20',
                    classNames?.[UI.Day],
                ),
                [UI.DayButton]: cn(
                    'h-9 w-9 rounded-md p-0 font-normal transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none disabled:pointer-events-none',
                    classNames?.[UI.DayButton],
                ),
                [SelectionState.selected]: cn(
                    '[&>button]:bg-primary [&>button]:text-primary-foreground [&>button]:hover:bg-primary [&>button]:hover:text-primary-foreground [&>button]:focus:bg-primary [&>button]:focus:text-primary-foreground',
                    classNames?.[SelectionState.selected],
                ),
                [SelectionState.range_start]: cn(
                    'rounded-l-md [&>button]:rounded-l-md',
                    classNames?.[SelectionState.range_start],
                ),
                [SelectionState.range_middle]: cn(
                    '[&>button]:rounded-none [&>button]:bg-accent [&>button]:text-accent-foreground',
                    classNames?.[SelectionState.range_middle],
                ),
                [SelectionState.range_end]: cn(
                    'rounded-r-md [&>button]:rounded-r-md',
                    classNames?.[SelectionState.range_end],
                ),
                [DayFlag.today]: cn(
                    '[&>button]:border [&>button]:border-primary/50',
                    classNames?.[DayFlag.today],
                ),
                [DayFlag.outside]: cn(
                    'text-muted-foreground opacity-50',
                    classNames?.[DayFlag.outside],
                ),
                [DayFlag.disabled]: cn(
                    'text-muted-foreground opacity-40 [&>button]:hover:bg-transparent [&>button]:hover:text-muted-foreground',
                    classNames?.[DayFlag.disabled],
                ),
                [DayFlag.hidden]: cn('invisible', classNames?.[DayFlag.hidden]),
                [DayFlag.focused]: cn(
                    '[&>button]:ring-2 [&>button]:ring-ring',
                    classNames?.[DayFlag.focused],
                ),
                ...classNames,
            }}
            components={{
                Chevron: ({ orientation, className: chevronClassName }) =>
                    orientation === 'left' ? (
                        <ChevronLeft
                            className={cn('h-4 w-4', chevronClassName)}
                        />
                    ) : (
                        <ChevronRight
                            className={cn('h-4 w-4', chevronClassName)}
                        />
                    ),
                ...props.components,
            }}
            {...props}
        />
    );
}

Calendar.displayName = 'Calendar';

export { Calendar };
